<script lang="ts">
import type { PointerDownOutsideEvent } from 'reka-ui'

import LineSearchMd from '@docue/docue-ui-v2/icons/LineSearchMd.vue'
import {
  ComboboxItem,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  Presence,
  TabsContent,
  TabsList,
  TabsRoot,
  TabsTrigger,
  VisuallyHidden,
} from 'reka-ui'

import type { CommandPaletteArgs } from '~/composables/events'

import IconSearchAi from '~/components/IconSearchAi.vue'
import iconDocueAi from '~/assets/images/icon-docue-ai.svg?url'

import AppCommandPaletteChat from './Chat/AppCommandPaletteChat.vue'

export type CommandPaletteTab = CommandPaletteArgs['tab']
</script>

<script setup lang="ts">
const { arePremadeTemplatesVisible } = useAuth()
const { t } = useI18n()
const isOpen = ref(false)

const route = useRoute()
watch(() => route.path, () => {
  isOpen.value = false
})

const { enabled: isFindingAssistantChatFeatureEnabled } = useFeatureEnabled('finding-assistant-chat')
const isFindingAssistantChatEnabled = computed(() => arePremadeTemplatesVisible.value && isFindingAssistantChatFeatureEnabled.value)

const isMac = /Mac|iPhone|iPod|iPad/i.test(navigator.platform)

const keys = useMagicKeys({
  passive: false,
  onEventFired(e) {
    const isKDown = (e.key === 'k' || e.key === 'K') && e.type === 'keydown'
    if (isKDown && (e.metaKey || e.ctrlKey) && !isOpen.value)
      e.preventDefault()
  },
})

const { activeWorkspaceId } = useAuth()
const searchQuery = useSessionStorage(`${unref(activeWorkspaceId) || ''}::search::query`, '')
const chat = useTemplateRef<InstanceType<typeof AppCommandPaletteChat>>('chat')

const MetaK = keys['Meta+K']
const CtrlK = keys['Ctrl+K']

const activeElement = useActiveElement()

watch([MetaK, CtrlK], (values) => {
  const isInputLikeElementActive = ['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement.value?.tagName ?? '')
  if (isInputLikeElementActive || !values.includes(true) || isOpen.value) {
    return
  }

  isOpen.value = true
})

const currentTab = ref<'search' | 'chat'>('search')
const isNewChatRequested = ref(false)

const chatQuery = ref('')

const handleTabChange = (tab: CommandPaletteTab) => {
  currentTab.value = tab === 'search' ? 'search' : 'chat'
  isNewChatRequested.value = tab === 'chat.new'
}

const { commandPalette } = useEvents()

commandPalette.on((event, args) => {
  if (event !== 'open')
    return

  isOpen.value = true
  chatQuery.value = args.query ?? ''
  handleTabChange(args.tab)
})

const { $posthog } = useNuxtApp()

const openNewChat = () => {
  $posthog.capture('command_palette_chat_selected')
  handleTabChange('chat.new')
  chatQuery.value = unref(searchQuery) || ''
}

watch(isOpen, (newVal) => {
  if (!newVal) {
    chatQuery.value = ''
    isNewChatRequested.value = false
  }
})

const handlePointerDownOutside = (e: PointerDownOutsideEvent) => {
  if (chat?.value?.isProcessing)
    e.preventDefault()
}
</script>

<template>
  <DialogRoot v-model:open="isOpen">
    <DialogTrigger as-child>
      <slot
        name="trigger"
        :ai-enabled="isFindingAssistantChatEnabled"
      >
        <button
          class="
            flex
            size-8
            items-center
            justify-center
            gap-2
            rounded-full
            border
            border-transparent
            hover:bg-gray-600/5
            md:h-auto
            md:w-full
            md:justify-start
            md:rounded-xl
            md:bg-gray-100
            md:px-3
            md:py-2
            md:hover:border-gray-200
            lg:w-4/5
            xl:w-3/5
          "
          @click="$posthog.capture('command_palette_opened')"
        >
          <component
            :is="isFindingAssistantChatEnabled ? IconSearchAi : LineSearchMd"
            class="size-5 shrink-0 text-gray-600"
          />
          <div class="hidden w-full md:flex">
            <span class="text-base text-gray-400">{{ isFindingAssistantChatEnabled ? `${t('appCommandPalette.title')}…` : t('search.placeholder') }}</span>
            <span class="ml-auto mr-0 rounded-md border border-black/10 bg-gray-50 px-2 py-1 text-xs text-gray-800 [&_kbd]:font-sans">
              <kbd>{{ isMac ? '⌘' : 'Ctrl+' }}</kbd>
              <kbd>K</kbd>
            </span>
          </div>
        </button>
      </slot>
    </DialogTrigger>

    <DialogPortal>
      <DialogOverlay class="fixed inset-0 z-30 bg-black/50" />
      <!-- eslint-disable tailwindcss/no-custom-classname -->
      <Presence :present="isOpen">
        <div class="dialog-content-wrapper fixed inset-0 z-30 grid max-h-dvh max-w-[100dvw]">
          <!-- eslint-enable tailwindcss/no-custom-classname -->
          <DialogContent
            class="flex w-full max-w-3xl flex-col self-start justify-self-center rounded-xl bg-white shadow-md [grid-area:search]"
            @pointer-down-outside="handlePointerDownOutside"
          >
            <VisuallyHidden>
              <DialogTitle>{{ isFindingAssistantChatEnabled ? t('appCommandPalette.title') : t('search.placeholder') }}</DialogTitle>
              <DialogDescription>{{ isFindingAssistantChatEnabled ? t('appCommandPalette.title') : t('search.placeholder') }}</DialogDescription>
            </VisuallyHidden>

            <TabsRoot
              v-model="currentTab"
              default-value="search"
              activation-mode="manual"
            >
              <VisuallyHidden>
                <TabsList>
                  <TabsTrigger value="search">
                    <span>{{ t('appCommandPalette.searchTrigger') }}</span>
                  </TabsTrigger>
                  <TabsTrigger
                    v-if="isFindingAssistantChatEnabled"
                    value="chat"
                  >
                    <span>{{ t('appCommandPalette.chatTrigger') }}</span>
                  </TabsTrigger>
                </TabsList>
              </VisuallyHidden>
              <TabsContent value="search">
                <AppCommandPaletteSearch
                  v-model:query="searchQuery"
                  @tab-change="handleTabChange"
                  @close-requested="isOpen = false"
                >
                  <template
                    v-if="isFindingAssistantChatEnabled"
                    #trailing-results="{ activeFilterType, query }"
                  >
                    <div
                      v-if="activeFilterType !== 'document' && query.trim().length"
                      class="w-full bg-white px-4 py-2"
                    >
                      <span class="text-sm font-medium">{{ t('appCommandPalette.search.assistantTitle') }}</span>
                    </div>
                    <ComboboxItem
                      v-if="query.trim().length && activeFilterType !== 'document'"
                      :value="{ _type: 'chat', id: 'chat.new' }"
                      class="flex w-full items-center justify-start gap-2 rounded-md px-4 py-2 data-[highlighted]:bg-gray-50"
                      @select.prevent="openNewChat()"
                    >
                      <div class="flex size-9 items-center justify-center rounded-full bg-red">
                        <img
                          :src="iconDocueAi"
                          class="ml-px size-9 mix-blend-plus-lighter"
                        >
                      </div>
                      <div class="select-none font-sans text-sm">
                        <span class="font-medium"><strong>{{ query }}</strong></span><span class="text-gray-600"> - {{ t('appCommandPalette.chat.title') }}</span>
                      </div>
                    </ComboboxItem>
                  </template>
                </AppCommandPaletteSearch>
              </TabsContent>

              <TabsContent
                v-if="isFindingAssistantChatEnabled"
                value="chat"
              >
                <AppCommandPaletteChat
                  ref="chat"
                  :query="chatQuery"
                  :is-new="isNewChatRequested"
                  @tab-change="handleTabChange"
                />
              </TabsContent>
            </TabsRoot>
          </DialogContent>
        </div>
      </Presence>
    </DialogPortal>
  </DialogRoot>
</template>

<style scoped>
.dialog-content-wrapper {
  grid-template-areas:
    '. . .'
    '. search .'
    '. . .';

  grid-template-rows: min(5vw, 80px) 1fr min(5vw, 80px);
  grid-template-columns: min(5vw, 80px) 1fr min(5vw, 80px);
}

@media (max-height: 600px) {
  .dialog-content-wrapper {
    grid-template-rows: min(2vh, 20px) 1fr min(2vh, 20px);
  }
}

@media (max-height: 400px) {
  .dialog-content-wrapper {
    grid-template-rows: 0px 100% 0px;
  }
}
</style>
